import type { Category, IsoDate, Transaction } from './model';
import { SPENDING_GROUPS, groupOf, spendingOf, type SpendingGroup } from './spending';
import { matchesOwnership, type OwnershipFilter } from './household';

/**
 * The analytics engine: pure calculations over entries, no UI, no database.
 * Screens (Home, Analytics, Budgets, exports) ask it; they never add up
 * money themselves.
 *
 * Rules every figure follows:
 *  - Spending is decided by `spendingOf` (brief §14–15): expenses and debt
 *    payments count, refunds subtract, transfers / income / already-tracked
 *    card bills are 0. So no purchase can be counted twice.
 *  - Months are calendar months, keyed "YYYY-MM".
 *  - Money is integer minor units. Averages are rounded to whole minor units.
 *  - Averages only use months since the budget's history starts (the month
 *    of its first entry): a budget started two months ago has a 2-month
 *    "6-month average", reported as such, not a figure watered down by
 *    months that never had data.
 */
export type MonthKey = string; // YYYY-MM

export interface Scope {
  categoryId?: string;
  /** With categoryId: null = entries without a subcategory. */
  subcategoryId?: string | null;
  group?: SpendingGroup;
}

export interface Change {
  current: number;
  previous: number;
  delta: number;
  /** delta / previous; null when there is nothing to compare against (previous ≤ 0). */
  ratio: number | null;
}

export interface Average {
  /** Rounded mean in minor units; null when no month of history is available. */
  value: number | null;
  /** Months actually averaged (≤ requested). */
  months: number;
  requested: number;
  /** All requested months were inside the budget's history. */
  complete: boolean;
}

export interface Mover {
  categoryId: string;
  /** Set when movers are computed per subcategory (null = the category's own entries). */
  subcategoryId?: string | null;
  current: number;
  previous: number;
  delta: number;
  ratio: number | null;
}

export interface MoverOptions {
  level?: 'category' | 'subcategory';
  /** How many to return (default 3). */
  limit?: number;
  /** Ignore moves smaller than this, in minor units (default 1: any real change). */
  minDelta?: number;
  /** Compare against this month instead of the previous one (e.g. the same month last year). */
  against?: MonthKey;
}

const pad = (n: number) => String(n).padStart(2, '0');

export function monthOf(date: IsoDate): MonthKey {
  return date.slice(0, 7);
}

export function shiftMonth(month: MonthKey, by: number): MonthKey {
  const index = Number(month.slice(0, 4)) * 12 + Number(month.slice(5, 7)) - 1 + by;
  return `${Math.floor(index / 12)}-${pad((index % 12) + 1)}`;
}

/** `count` months ending with `last`, oldest first. */
export function monthsEnding(last: MonthKey, count: number): MonthKey[] {
  return Array.from({ length: count }, (_, i) => shiftMonth(last, i - count + 1));
}

const daysIn = (month: MonthKey) => new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0)).getUTCDate();

export function changeOf(current: number, previous: number): Change {
  const delta = current - previous;
  return { current, previous, delta, ratio: previous > 0 ? delta / previous : null };
}

const subKey = (id: string | null) => id ?? '';

/** What the analytics screen can narrow entries to (every field optional). */
export interface EntryFilter {
  categoryId?: string;
  subcategoryId?: string;
  /** The member who paid. */
  paidBy?: string;
  /** Whose it is: the household's (shared) or one member's own. */
  ownership?: OwnershipFilter;
}

/** Entries, or bills (a bill paid by whoever records it has no payer and matches no payer filter). */
export function filterEntries<T extends Pick<Transaction, 'categoryId' | 'subcategoryId' | 'ownership' | 'ownerMemberId'> & { paidBy: string | null }>(
  entries: readonly T[],
  filter: EntryFilter,
): T[] {
  return entries.filter(
    (e) =>
      (!filter.categoryId || e.categoryId === filter.categoryId) &&
      (!filter.subcategoryId || e.subcategoryId === filter.subcategoryId) &&
      (!filter.paidBy || e.paidBy === filter.paidBy) &&
      (!filter.ownership || matchesOwnership(e, filter.ownership)),
  );
}

/**
 * Build once per data set, ask many questions. Entries are indexed by
 * month → category → subcategory, with the day kept so a month can be cut
 * at a day for like-for-like comparisons.
 */
export function createAnalytics(
  entries: readonly Transaction[],
  categories: ReadonlyMap<string, Pick<Category, 'kind' | 'nature'>>,
  options: {
    /**
     * Where the budget's history starts when `entries` are a filtered part
     * of it: a member's first payment is not where the budget began, and an
     * average must not skip months just because this filter had none.
     */
    historyStart?: MonthKey | null;
  } = {},
) {
  // month → categoryId → subcategoryKey → list of [day, signed amount]
  const cube = new Map<MonthKey, Map<string, Map<string, [number, number][]>>>();
  let firstMonth: MonthKey | null = options.historyStart ?? null;
  const fixedStart = options.historyStart !== undefined;

  for (const e of entries) {
    const month = monthOf(e.date);
    if (!fixedStart && (firstMonth === null || month < firstMonth)) firstMonth = month;
    const v = spendingOf(e);
    if (v === 0) continue;
    let cats = cube.get(month);
    if (!cats) cube.set(month, (cats = new Map()));
    let subs = cats.get(e.categoryId);
    if (!subs) cats.set(e.categoryId, (subs = new Map()));
    const key = subKey(e.subcategoryId);
    let list = subs.get(key);
    if (!list) subs.set(key, (list = []));
    list.push([Number(e.date.slice(8, 10)), v]);
  }

  const inScope = (categoryId: string, sub: string, scope: Scope) =>
    (scope.categoryId === undefined || scope.categoryId === categoryId) &&
    (scope.subcategoryId === undefined || subKey(scope.subcategoryId) === sub) &&
    (scope.group === undefined || groupOf(categories.get(categoryId) as Category | undefined) === scope.group);

  /** Sum of a month within a scope, optionally only days 1..throughDay. */
  function sum(month: MonthKey, scope: Scope = {}, throughDay = 31): number {
    let total = 0;
    for (const [categoryId, subs] of cube.get(month) ?? []) {
      for (const [sub, list] of subs) {
        if (!inScope(categoryId, sub, scope)) continue;
        for (const [day, v] of list) if (day <= throughDay) total += v;
      }
    }
    return total;
  }

  /** Per category (or category/subcategory) sums of a month. */
  function breakdown(month: MonthKey, level: 'category' | 'subcategory', scope: Scope = {}): Map<string, number> {
    const out = new Map<string, number>();
    for (const [categoryId, subs] of cube.get(month) ?? []) {
      for (const [sub, list] of subs) {
        if (!inScope(categoryId, sub, scope)) continue;
        const key = level === 'category' ? categoryId : `${categoryId}/${sub}`;
        out.set(key, (out.get(key) ?? 0) + list.reduce((s, [, v]) => s + v, 0));
      }
    }
    return out;
  }

  /** "As of" a date inside `month`: the day to cut both months at (clamped to each month's length). */
  const cutDay = (asOf: IsoDate | undefined, month: MonthKey) => (asOf && monthOf(asOf) === month ? Number(asOf.slice(8, 10)) : 31);

  function compare(month: MonthKey, against: MonthKey, scope: Scope, asOf?: IsoDate): Change {
    const day = cutDay(asOf, month);
    return changeOf(sum(month, scope, day), sum(against, scope, day === 31 ? 31 : Math.min(day, daysIn(against))));
  }

  function average(month: MonthKey, n: number, scope: Scope = {}, { includeCurrent = false } = {}): Average {
    const last = includeCurrent ? month : shiftMonth(month, -1);
    const window = monthsEnding(last, n).filter((m) => firstMonth !== null && m >= firstMonth);
    if (window.length === 0) return { value: null, months: 0, requested: n, complete: false };
    const total = window.reduce((s, m) => s + sum(m, scope), 0);
    return { value: Math.round(total / window.length), months: window.length, requested: n, complete: window.length === n };
  }

  function movers(month: MonthKey, options: MoverOptions = {}): { increases: Mover[]; decreases: Mover[] } {
    const { level = 'category', limit = 3, minDelta = 1, against = shiftMonth(month, -1) } = options;
    const now = breakdown(month, level);
    const before = breakdown(against, level);
    const all: Mover[] = [...new Set([...now.keys(), ...before.keys()])].map((key) => {
      const [categoryId, sub] = key.split('/') as [string, string | undefined];
      const c = changeOf(now.get(key) ?? 0, before.get(key) ?? 0);
      return level === 'subcategory' ? { categoryId, subcategoryId: sub === '' ? null : sub!, ...c } : { categoryId, ...c };
    });
    const tie = (a: Mover, b: Mover) => `${a.categoryId}/${a.subcategoryId ?? ''}`.localeCompare(`${b.categoryId}/${b.subcategoryId ?? ''}`);
    return {
      increases: all.filter((m) => m.delta >= minDelta).sort((a, b) => b.delta - a.delta || tie(a, b)).slice(0, limit),
      decreases: all.filter((m) => m.delta <= -minDelta).sort((a, b) => a.delta - b.delta || tie(a, b)).slice(0, limit),
    };
  }

  return {
    /** First month with any entry (null for an empty budget). */
    historyStart: firstMonth,

    monthlyTotal: (month: MonthKey, scope?: Scope) => sum(month, scope),
    /** Monthly totals for a run of months (oldest first), e.g. a trend line. */
    monthlyTotals: (months: readonly MonthKey[], scope?: Scope) => months.map((month) => ({ month, total: sum(month, scope) })),

    categoryTotal: (month: MonthKey, categoryId: string) => sum(month, { categoryId }),
    /** Every category with spending in the month, largest first (refund-only categories left out). */
    categoryTotals: (month: MonthKey) =>
      [...breakdown(month, 'category')]
        .filter(([, v]) => v > 0)
        .map(([categoryId, total]) => ({ categoryId, total }))
        .sort((a, b) => b.total - a.total || a.categoryId.localeCompare(b.categoryId)),

    subcategoryTotal: (month: MonthKey, categoryId: string, subcategoryId: string | null) => sum(month, { categoryId, subcategoryId }),
    /** A category's subcategories in the month, largest first; null = entries on the category itself. */
    subcategoryTotals: (month: MonthKey, categoryId: string) =>
      [...breakdown(month, 'subcategory', { categoryId })]
        .filter(([, v]) => v > 0)
        .map(([key, total]) => {
          const sub = key.slice(key.indexOf('/') + 1);
          return { subcategoryId: sub === '' ? null : sub, total };
        })
        .sort((a, b) => b.total - a.total || (a.subcategoryId ?? '').localeCompare(b.subcategoryId ?? '')),

    fixedTotal: (month: MonthKey) => sum(month, { group: 'fixed' }),
    variableTotal: (month: MonthKey) => sum(month, { group: 'variable' }),
    obligationsTotal: (month: MonthKey) => sum(month, { group: 'obligations' }),
    groupTotals: (month: MonthKey) => Object.fromEntries(SPENDING_GROUPS.map((g) => [g, sum(month, { group: g })])) as Record<SpendingGroup, number>,

    /**
     * Month over month. With `asOf` (a date in `month`), both months are cut
     * at that day: 12 days of September against the first 12 days of August,
     * not against all of August.
     */
    mom: (month: MonthKey, scope: Scope = {}, asOf?: IsoDate) => compare(month, shiftMonth(month, -1), scope, asOf),
    /** Year over year: the same month a year earlier (cut at `asOf` like mom). */
    yoy: (month: MonthKey, scope: Scope = {}, asOf?: IsoDate) => compare(month, shiftMonth(month, -12), scope, asOf),

    /**
     * Running total through the month, one value per day (index 0 = the
     * 1st): how fast the month is being spent, for pace comparisons.
     */
    cumulativeDaily(month: MonthKey, scope: Scope = {}): number[] {
      const days = new Array<number>(daysIn(month)).fill(0);
      for (const [categoryId, subs] of cube.get(month) ?? []) {
        for (const [sub, list] of subs) {
          if (!inScope(categoryId, sub, scope)) continue;
          for (const [day, v] of list) days[Math.min(day, days.length) - 1]! += v;
        }
      }
      for (let i = 1; i < days.length; i++) days[i]! += days[i - 1]!;
      return days;
    },

    /** Mean of the n months before `month` (or ending with it: includeCurrent). */
    average,
    average3: (month: MonthKey, scope?: Scope) => average(month, 3, scope),
    average6: (month: MonthKey, scope?: Scope) => average(month, 6, scope),
    average12: (month: MonthKey, scope?: Scope) => average(month, 12, scope),

    /** Biggest moves against the previous month (or `against`), per category or subcategory. */
    movers,
    topIncreases: (month: MonthKey, options?: MoverOptions) => movers(month, options).increases,
    topDecreases: (month: MonthKey, options?: MoverOptions) => movers(month, options).decreases,
  };
}

export type Analytics = ReturnType<typeof createAnalytics>;
